import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { EditPackageTypePayload, PackageType } from 'src/interfaces/medicine';

@Component({
  selector: 'core-package-type-manage-list',
  templateUrl: './package-type-manage-list.component.html',
  styleUrls: ['./package-type-manage-list.component.scss'],
  standalone: false
})
export class PackageTypeManageListComponent implements OnInit, OnDestroy {

  @Output() packageTypesChange = new EventEmitter<PackageType[]>();
  @Output() createRequested = new EventEmitter<void>();

  packageTypes: PackageType[] = [];
  searchControl = new FormControl('');
  isSearching = false;
  editingPackageType: PackageType | null = null;

  editForm = this._formBuilder.group({
    type: ['', Validators.required],
    code: ['', Validators.required]
  });

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(
    private _medicineService: MedicineService,
    private _formBuilder: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.loadPackageTypes('');

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((search: string | null) => {
        this.loadPackageTypes(search ?? '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadPackageTypes(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.packageTypes = await this._medicineService.getPackageTypes(1, this.searchLimit, search);
      this.packageTypesChange.emit(this.packageTypes);
    } finally {
      this.isSearching = false;
    }
  }

  startEdit(packageType: PackageType): void {
    this.editingPackageType = packageType;
    this.editForm.patchValue({
      type: packageType.type,
      code: packageType.code
    });
  }

  cancelEdit(): void {
    this.editingPackageType = null;
    this.editForm.reset();
  }

  async saveEdit(): Promise<void> {
    if (!this.editingPackageType || !this.editForm.valid) {
      return;
    }

    const payload: EditPackageTypePayload = {
      id: this.editingPackageType.id,
      type: this.editForm.value.type,
      code: this.editForm.value.code
    };

    const success: boolean = await this._medicineService.editPackageType(payload);

    if (success) {
      this.editingPackageType = null;
      this.editForm.reset();
      await this.loadPackageTypes(this.searchControl.value ?? '');
    }
  }

  async deletePackageType(packageType: PackageType): Promise<void> {
    const success: boolean = await this._medicineService.deletePackageType(packageType.id);

    if (success) {
      if (this.editingPackageType?.id === packageType.id) {
        this.cancelEdit();
      }

      this.packageTypes = this.packageTypes.filter(type => type.id !== packageType.id);
      this.packageTypesChange.emit(this.packageTypes);
    }
  }

}
